/**
 * GroupInquiryModal — 组团游产品客户留咨弹窗组件（单例模式）
 *
 * 设计要点
 *   1. 单例：snippet 可以在页面任意位置被 render 任意多次，每个 <script src> 浏览器
 *      都会各自执行一遍（同名 src 不跨标签去重）。这里用 window 守卫让第二次及以后
 *      的执行整体 no-op；弹窗 DOM 只由本模块创建，永远只有一个。
 *   2. 懒挂载 + 关闭即释放：第一次点击入口才 build DOM，close() 时整体销毁
 *      （移除节点、解绑 document 监听、恢复 body 滚动）。表单值另存在 JS 里，
 *      重开时回填，所以销毁 DOM 不会丢用户已填内容。
 *   3. 回退页面释放：pagehide（含 bfcache 场景）触发 release()。
 *   4. 入口解耦：document 上一次性委托 [data-gim-trigger]，新增入口无需再绑事件。
 *
 * 视觉与交互令牌见 assets/dd-group-inquiry-modal.css（--dd-* 系列）
 */

;(function () {
  'use strict'

  // 单例守卫：重复执行会让 document 上的入口委托指向新闭包，
  // 而旧实例的弹窗节点仍在页面上，表现为「点开是空的」。
  if (window.GroupInquiryModal) return

  /* ======================================================================
     常量与数据
     ====================================================================== */

  var POPUP_ID = 'ddGroupInquiryModal'
  var MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December']
  var MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  var WEEKDAY_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
  var WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  // 酒店标准：value 是提交给接口的字符串，按接口约定用中文星级
  // （API接入.md 里 hotel_standard 示例为 "4星"）；label 是界面上展示的英文，两者刻意分开。
  var HOTEL_OPTIONS = [
    { value: '3星', label: 'hotel3', stars: 3 },
    { value: '4星', label: 'hotel4', stars: 4 },
    { value: '5星', label: 'hotel5', stars: 5 },
  ]

  // 出行人数下拉：首项既是「没选」时的默认值，也是真正提交的字符串
  // （接口示例里 babies 就是 "Babies"），所以选项的显示文案与 value 相同。
  var ADULT_VALUES = ['Adults', '1', '2', '3', '4', '5', '6', '7', '8', '9', '≥10']
  var CHILD_VALUES = ['Children', '1', '2', '3', '4', '5', '6']
  var BABY_VALUES = ['Babies', '1', '2', '3', '4', '5', '6']

  var DEFAULT_LABELS = {
    eyebrow: "You're inquiring about",
    codeLabel: 'Tour Code',
    close: 'Close',
    detailsLegend: 'Your Details',
    tripLegend: 'Trip Details',

    name: 'Your Full Name',
    namePlaceholder: 'Jane Doe',
    nationality: 'Nationality',
    nationalityPlaceholder: 'United States',
    email: 'Email',
    emailPlaceholder: 'your@email.com',
    phone: 'Phone Number',
    phoneHint: "(We'll only call if you ask.)",
    phonePlaceholder: '415 555 0132',
    phoneCode: 'Country code',

    travelDate: 'Your Travel Date',
    travelDatePlaceholder: 'Select a date',
    travelers: 'No. of Travelers',
    adults: 'Adults',
    adultsNote: '≥12 years old',
    children: 'Children',
    childrenNote: '2~11 years old',
    babies: 'Babies',
    babiesNote: '<2 years old',
    hotel: 'Hotel Class',
    hotel3: '3 Star',
    hotel4: '4 Star',
    hotel5: '5 Star',
    message: 'Tell us your travel requirements / questions',
    messagePlaceholder: 'Our must-dos / must-sees for this trip are… Tell us about anniversaries, honeymoons, family travel, birthdays…',

    note: 'Free and no obligation · We reply within 24 hours',
    submit: 'Send Inquiry',
    submitting: 'Sending…',

    successTitle: 'Thank you!',
    successText: 'Your inquiry has been sent. Our travel experts will get back to you within 24 hours.',
    successClose: 'Close',

    errRequired: 'This field is required.',
    errEmail: 'Please enter a valid email address.',
    errPhone: 'Please enter a valid phone number.',
    errNoApi: 'Inquiry service is not configured yet. Please email us instead.',
    errNetwork: 'Submission failed. Please try again, or email us directly.',

    calPrev: 'Previous month',
    calNext: 'Next month',
    calToday: 'Today',
    calClear: 'Clear',
  }

  /* ======================================================================
     工具
     ====================================================================== */

  function esc(v) {
    return String(v == null ? '' : v)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  function todayFloor() {
    var d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }

  function fmtISO(date) {
    var m = String(date.getMonth() + 1)
    var d = String(date.getDate())
    return date.getFullYear() + '-' + (m.length < 2 ? '0' + m : m) + '-' + (d.length < 2 ? '0' + d : d)
  }

  function parseISO(value) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''))
    if (!m) return null
    var d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    d.setHours(0, 0, 0, 0)
    return isNaN(d.getTime()) ? null : d
  }

  function fmtDisplay(date) {
    return MONTH_SHORT[date.getMonth()] + ' ' + date.getDate() + ', ' + date.getFullYear() +
           ' (' + WEEKDAY_NAMES[date.getDay()] + ')'
  }

  function isEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)
  }

  function onlyDigits(v) {
    return String(v || '').replace(/\D/g, '')
  }

  function normalizeConfig(raw) {
    raw = raw || {}
    var labels = {}
    var key
    for (key in DEFAULT_LABELS) {
      if (Object.prototype.hasOwnProperty.call(DEFAULT_LABELS, key)) labels[key] = DEFAULT_LABELS[key]
    }
    if (raw.labels) {
      for (key in raw.labels) {
        if (Object.prototype.hasOwnProperty.call(raw.labels, key) && raw.labels[key]) labels[key] = raw.labels[key]
      }
    }
    return {
      productName: raw.productName || '',
      productCode: raw.productCode || '',
      apiUrl: raw.apiUrl || '',
      labels: labels,
    }
  }

  /* ======================================================================
     状态
     ====================================================================== */

  var cfg = normalizeConfig(null)
  var elPopup = null
  var refs = {}
  var values = {}          // 表单值（销毁 DOM 后仍保留）
  var valKeyMap = {}       // data-gim-key -> 控件
  var radioGroups = {}     // data-gim-key -> 单选组里的全部 input（见 cacheRefs 的说明）
  var lastFocused = null
  var prevBodyOverflow = ''
  var submitting = false
  var calEl = null
  var cal = { year: 0, month: 0 }
  var calOpenedAt = 0

  /* ======================================================================
     Markup
     ====================================================================== */

  /** 出行人数下拉：首项即占位项（value 等于自身文案），不再另加空 value 的选项 */
  function optionsHtml(list) {
    var html = ''
    for (var i = 0; i < list.length; i++) {
      html += '<option value="' + esc(list[i]) + '">' + esc(list[i]) + '</option>'
    }
    return html
  }

  /** 区号表由 assets/dd-country-codes.js 统一维护，两个留咨弹窗共用 */
  function countryOptionsHtml() {
    // 现取而不是启动时缓存成变量：即便脚本顺序被调整，也不会永久拿到空表。
    // 表项格式：[区号, ISO 二字代码, 国家名称]，已按名称 A→Z 排好
    var list = window.DDCountryCodes || []
    if (!list.length) {
      console.error('[GroupInquiryModal] 未加载 assets/dd-country-codes.js，区号下拉为空。')
    }
    // 表按 A→Z 排，United States 不在首位；沿用旧行为，把美国显式标成默认项。
    // option 的 value 用 ISO 二字代码而不是区号：+1 有 25 个国家/地区，用区号做
    // value 时「重开时回填」只能命中同值的第一个（Canada 会变成 American Samoa）。
    // 选项文案是「ISO 代码 (+区号)」，例如 CN (+86)。
    var html = ''
    for (var i = 0; i < list.length; i++) {
      var c = list[i]
      html += '<option value="' + esc(c[1]) + '"' + (c[1] === 'US' ? ' selected' : '') +
        ' data-dial="' + esc(c[0]) + '">' + esc(c[1] + ' (' + c[0] + ')') + '</option>'
    }
    return html
  }

  function hotelRadiosHtml(L) {
    var html = ''
    for (var i = 0; i < HOTEL_OPTIONS.length; i++) {
      var o = HOTEL_OPTIONS[i]
      var stars = ''
      for (var s = 0; s < o.stars; s++) stars += '★'
      html +=
        '<label class="gim-radio">' +
          '<input type="radio" name="hotel_standard" value="' + esc(o.value) + '" data-gim-key="hotelClass">' +
          '<span class="gim-radio__box">' +
            '<span class="gim-radio__stars" aria-hidden="true">' + stars + '</span>' +
            esc(L[o.label]) +
          '</span>' +
        '</label>'
    }
    return html
  }

  function detailFieldsHtml(L) {
    return '' +
      '<div class="gim-field">' +
        '<label class="gim-label" for="gimName">' + esc(L.name) +
          '<span class="gim-req" aria-hidden="true">*</span></label>' +
        '<input class="gim-input" id="gimName" name="name" type="text" required' +
          ' autocomplete="name" placeholder="' + esc(L.namePlaceholder) + '"' +
          ' data-gim-key="name" aria-describedby="gimNameErr">' +
        '<p class="gim-error" id="gimNameErr" role="alert"></p>' +
      '</div>' +

      '<div class="gim-field">' +
        '<label class="gim-label" for="gimNationality">' + esc(L.nationality) + '</label>' +
        '<input class="gim-input" id="gimNationality" name="nationality" type="text"' +
          ' autocomplete="country-name" placeholder="' + esc(L.nationalityPlaceholder) + '"' +
          ' data-gim-key="nationality">' +
      '</div>' +

      '<div class="gim-field">' +
        '<label class="gim-label" for="gimEmail">' + esc(L.email) +
          '<span class="gim-req" aria-hidden="true">*</span></label>' +
        '<input class="gim-input" id="gimEmail" name="email" type="email" required' +
          ' autocomplete="email" placeholder="' + esc(L.emailPlaceholder) + '"' +
          ' data-gim-key="email" aria-describedby="gimEmailErr">' +
        '<p class="gim-error" id="gimEmailErr" role="alert"></p>' +
      '</div>' +

      '<div class="gim-field">' +
        '<label class="gim-label" for="gimPhone">' + esc(L.phone) +
          '<span class="gim-req" aria-hidden="true">*</span>' +
          '<span class="gim-hint">' + esc(L.phoneHint) + '</span></label>' +
        '<div class="gim-phone">' +
          '<select class="gim-select" id="gimPhoneCode" name="country_code"' +
            ' aria-label="' + esc(L.phoneCode) + '" data-gim-key="phoneCode">' +
            countryOptionsHtml() +
          '</select>' +
          '<input class="gim-input" id="gimPhone" name="phone" type="tel" required' +
            ' inputmode="numeric" autocomplete="tel-national" placeholder="' + esc(L.phonePlaceholder) + '"' +
            ' data-gim-key="phone" aria-describedby="gimPhoneErr">' +
        '</div>' +
        '<p class="gim-error" id="gimPhoneErr" role="alert"></p>' +
      '</div>'
  }

  function tripFieldsHtml(L) {
    return '' +
      '<div class="gim-field">' +
        '<span class="gim-label">' + esc(L.travelDate) + '</span>' +
        '<button type="button" class="gim-date-btn" id="gimDateBtn"' +
          ' aria-haspopup="dialog" aria-expanded="false">' +
          '<span class="gim-date-btn__text" id="gimDateText">' + esc(L.travelDatePlaceholder) + '</span>' +
          '<svg class="gim-date-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"' +
            ' stroke-width="1.8" stroke-linecap="round" aria-hidden="true">' +
            '<rect x="3" y="5" width="18" height="16" rx="2"></rect>' +
            '<path d="M8 3v4M16 3v4M3 11h18"></path>' +
          '</svg>' +
        '</button>' +
        '<input type="hidden" id="gimDate" name="travel_date" value="" data-gim-key="travelDate">' +
      '</div>' +

      '<div class="gim-field">' +
        '<span class="gim-label" id="gimTravelersLabel">' + esc(L.travelers) + '</span>' +
        '<div class="gim-travelers" role="group" aria-labelledby="gimTravelersLabel">' +
          '<div class="gim-traveler">' +
            '<select class="gim-select" id="gimAdults" name="adults"' +
              ' aria-label="' + esc(L.adults + ' ' + L.adultsNote) + '" data-gim-key="adults">' +
              optionsHtml(ADULT_VALUES) +
            '</select>' +
            '<small>' + esc(L.adultsNote) + '</small>' +
          '</div>' +
          '<div class="gim-traveler">' +
            '<select class="gim-select" id="gimChildren" name="children"' +
              ' aria-label="' + esc(L.children + ' ' + L.childrenNote) + '" data-gim-key="children">' +
              optionsHtml(CHILD_VALUES) +
            '</select>' +
            '<small>' + esc(L.childrenNote) + '</small>' +
          '</div>' +
          '<div class="gim-traveler">' +
            '<select class="gim-select" id="gimBabies" name="babies"' +
              ' aria-label="' + esc(L.babies + ' ' + L.babiesNote) + '" data-gim-key="babies">' +
              optionsHtml(BABY_VALUES) +
            '</select>' +
            '<small>' + esc(L.babiesNote) + '</small>' +
          '</div>' +
        '</div>' +
      '</div>' +

      '<div class="gim-field">' +
        '<span class="gim-label" id="gimHotelLabel">' + esc(L.hotel) + '</span>' +
        '<div class="gim-radio-row" role="radiogroup" aria-labelledby="gimHotelLabel">' +
          hotelRadiosHtml(L) +
        '</div>' +
      '</div>' +

      '<div class="gim-field">' +
        '<label class="gim-label" for="gimMessage">' + esc(L.message) + '</label>' +
        '<textarea class="gim-textarea" id="gimMessage" name="message" rows="3"' +
          ' placeholder="' + esc(L.messagePlaceholder) + '" data-gim-key="message"></textarea>' +
      '</div>'
  }

  function buildMarkup() {
    var L = cfg.labels
    var product = cfg.productName || '—'
    return '' +
      '<div class="gim-backdrop" data-gim-close></div>' +
      '<div class="gim-dialog" tabindex="-1">' +
        '<div class="gim-header">' +
          '<div class="gim-header-text">' +
            '<span class="gim-eyebrow">' + esc(L.eyebrow) + '</span>' +
            '<h2 class="gim-title" id="gimTitle">' + esc(product) + '</h2>' +
            '<span class="gim-code">' + esc(L.codeLabel) +
              '<strong>' + esc(cfg.productCode || '—') + '</strong></span>' +
          '</div>' +
          '<button type="button" class="gim-close" data-gim-close' +
            ' aria-label="' + esc(L.close) + '">&times;</button>' +
        '</div>' +

        '<form class="gim-form" id="gimForm" novalidate>' +
          '<div class="gim-body">' +
            '<div class="gim-grid">' +
              '<fieldset class="gim-block">' +
                '<legend class="gim-legend">' + esc(L.detailsLegend) + '</legend>' +
                detailFieldsHtml(L) +
              '</fieldset>' +
              '<fieldset class="gim-block">' +
                '<legend class="gim-legend">' + esc(L.tripLegend) + '</legend>' +
                tripFieldsHtml(L) +
              '</fieldset>' +
            '</div>' +
          '</div>' +
          '<div class="gim-status" id="gimStatus" role="status" aria-live="polite"></div>' +
          '<div class="gim-footer">' +
            '<p class="gim-note">' + esc(L.note) + '</p>' +
            '<button type="submit" class="gim-submit" id="gimSubmit">' + esc(L.submit) + '</button>' +
          '</div>' +
        '</form>' +

        '<div class="gim-success" id="gimSuccess" hidden>' +
          '<span class="gim-success__icon">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"' +
              ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
              '<path d="M20 6L9 17l-5-5"></path>' +
            '</svg>' +
          '</span>' +
          '<h3 class="gim-success__title">' + esc(L.successTitle) + '</h3>' +
          '<p class="gim-success__text">' + esc(L.successText) + '<br>' +
            '<strong>' + esc(product) + '</strong></p>' +
          '<button type="button" class="gim-submit" data-gim-close>' + esc(L.successClose) + '</button>' +
        '</div>' +
      '</div>'
  }

  /* ======================================================================
     表单值：读写与跨开关保留
     ====================================================================== */

  function cacheRefs() {
    refs = {
      dialog: elPopup.querySelector('.gim-dialog'),
      form: document.getElementById('gimForm'),
      status: document.getElementById('gimStatus'),
      submit: document.getElementById('gimSubmit'),
      success: document.getElementById('gimSuccess'),
      name: document.getElementById('gimName'),
      nameErr: document.getElementById('gimNameErr'),
      nationality: document.getElementById('gimNationality'),
      email: document.getElementById('gimEmail'),
      emailErr: document.getElementById('gimEmailErr'),
      phoneCode: document.getElementById('gimPhoneCode'),
      phone: document.getElementById('gimPhone'),
      phoneErr: document.getElementById('gimPhoneErr'),
      dateBtn: document.getElementById('gimDateBtn'),
      dateText: document.getElementById('gimDateText'),
      date: document.getElementById('gimDate'),
      adults: document.getElementById('gimAdults'),
      children: document.getElementById('gimChildren'),
      babies: document.getElementById('gimBabies'),
      message: document.getElementById('gimMessage'),
    }
    valKeyMap = {}
    radioGroups = {}
    var nodes = elPopup.querySelectorAll('[data-gim-key]')
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i]
      var key = node.getAttribute('data-gim-key')
      if (node.type === 'radio') {
        // 同名单选组有多个元素，而「勾哪个」只能由「值相等的那个」决定。
        // 若照其它控件那样只留最后一个节点，回填/读取就永远只作用于最后一颗单选，
        // 选中 3 星后关闭再打开会表现为「没选」。
        if (!radioGroups[key]) radioGroups[key] = []
        radioGroups[key].push(node)
      } else {
        valKeyMap[key] = node
      }
    }
  }

  function applyValues() {
    for (var key in values) {
      if (!Object.prototype.hasOwnProperty.call(values, key)) continue
      var el = valKeyMap[key]
      if (!el) continue
      el.value = values[key]
    }
    setCheckedRadio(values)
    // 日期按钮的文案由隐藏 input 派生，单独刷新
    renderDateText()
  }

  /** 按 values 勾选每个单选组里值相等的那一颗 */
  function setCheckedRadio(source) {
    for (var key in radioGroups) {
      if (!Object.prototype.hasOwnProperty.call(radioGroups, key)) continue
      var group = radioGroups[key]
      for (var i = 0; i < group.length; i++) {
        group[i].checked = group[i].value === source[key]
      }
    }
  }

  function readForm() {
    for (var key in valKeyMap) {
      if (!Object.prototype.hasOwnProperty.call(valKeyMap, key)) continue
      values[key] = valKeyMap[key].value
    }
    for (var rk in radioGroups) {
      if (!Object.prototype.hasOwnProperty.call(radioGroups, rk)) continue
      var group = radioGroups[rk]
      for (var i = 0; i < group.length; i++) {
        // 一颗都没勾时保留原值，避免把「本来没选」变成空串覆盖掉草稿
        if (group[i].checked) values[rk] = group[i].value
      }
    }
  }

  function onFormInput(e) {
    var el = e.target
    if (!el || !el.getAttribute) return
    var key = el.getAttribute('data-gim-key')
    if (!key) return
    if (el.type === 'radio') {
      if (el.checked) values[key] = el.value
    } else {
      values[key] = el.value
    }
    // 一旦用户开始修正，立即收掉该字段的错误提示
    clearFieldError(el)
  }

  function renderDateText() {
    if (!refs.date || !refs.dateText) return
    var d = parseISO(refs.date.value)
    if (d) {
      refs.dateText.textContent = fmtDisplay(d)
      refs.dateBtn.classList.add('has-value')
    } else {
      refs.dateText.textContent = cfg.labels.travelDatePlaceholder
      refs.dateBtn.classList.remove('has-value')
    }
  }

  /* ======================================================================
     日历弹层
     ====================================================================== */

  var CAL_ID = 'gimCalPanel'

  function buildCalMarkup() {
    var L = cfg.labels
    var html =
      '<div class="gim-cal-head">' +
        '<button type="button" class="gim-cal-nav" data-cal="prev"' +
          ' aria-label="' + esc(L.calPrev) + '">&lsaquo;</button>' +
        '<span class="gim-cal-title" id="gimCalTitle"></span>' +
        '<button type="button" class="gim-cal-nav" data-cal="next"' +
          ' aria-label="' + esc(L.calNext) + '">&rsaquo;</button>' +
      '</div>' +
      '<div class="gim-cal-week">'
    for (var i = 0; i < WEEKDAY_SHORT.length; i++) html += '<span>' + WEEKDAY_SHORT[i] + '</span>'
    html += '</div>' +
      '<div class="gim-cal-grid" id="gimCalGrid"></div>' +
      '<div class="gim-cal-foot">' +
        '<button type="button" class="gim-cal-link" data-cal="today">' + esc(L.calToday) + '</button>' +
        '<button type="button" class="gim-cal-link" data-cal="clear">' + esc(L.calClear) + '</button>' +
      '</div>'
    return html
  }

  function openCal() {
    if (calEl) { closeCal(); return }
    var base = parseISO(refs.date.value) || todayFloor()
    cal.year = base.getFullYear()
    cal.month = base.getMonth()

    calEl = document.createElement('div')
    calEl.id = CAL_ID
    calEl.className = 'gim-cal-panel'
    calEl.setAttribute('role', 'dialog')
    calEl.setAttribute('aria-label', cfg.labels.travelDate)
    calEl.innerHTML = buildCalMarkup()
    document.body.appendChild(calEl)

    calEl.addEventListener('click', onCalClick)
    renderCal()
    positionCal()
    // 打开动画结束前不监听外部点击，否则触发按钮自身的那次 click 会立刻把它关掉
    calOpenedAt = Date.now()
    window.addEventListener('resize', positionCal)
    window.addEventListener('scroll', positionCal, true)
    setTimeout(function () { document.addEventListener('mousedown', onDocMouseDownCal) }, 0)
    refs.dateBtn.setAttribute('aria-expanded', 'true')

    var firstCell = calEl.querySelector('.gim-cal-cell:not(:disabled):not(.is-empty)')
    if (firstCell) firstCell.focus()
  }

  function closeCal() {
    if (!calEl) return
    if (refs.dateBtn) refs.dateBtn.setAttribute('aria-expanded', 'false')
    calEl.removeEventListener('click', onCalClick)
    calEl.remove()
    calEl = null
    window.removeEventListener('resize', positionCal)
    window.removeEventListener('scroll', positionCal, true)
    document.removeEventListener('mousedown', onDocMouseDownCal)
  }

  function onDocMouseDownCal(e) {
    if (!calEl) return
    if (Date.now() - calOpenedAt < 200) return
    if (calEl.contains(e.target)) return
    if (refs.dateBtn && refs.dateBtn.contains(e.target)) return
    closeCal()
  }

  function positionCal() {
    if (!calEl || !refs.dateBtn) return
    var r = refs.dateBtn.getBoundingClientRect()
    var w = calEl.offsetWidth || 296
    var h = calEl.offsetHeight || 328
    var left = Math.min(Math.max(8, r.left + r.width / 2 - w / 2), window.innerWidth - w - 8)
    var top = r.bottom + 6
    if (top + h > window.innerHeight - 8) top = Math.max(8, r.top - h - 6)
    calEl.style.left = Math.max(8, left) + 'px'
    calEl.style.top = top + 'px'
  }

  function renderCal() {
    if (!calEl) return
    var titleEl = calEl.querySelector('#gimCalTitle')
    var gridEl = calEl.querySelector('#gimCalGrid')
    if (!titleEl || !gridEl) return

    titleEl.textContent = MONTH_NAMES[cal.month] + ' ' + cal.year

    var today = todayFloor()
    var selected = parseISO(refs.date ? refs.date.value : '')
    var firstWd = new Date(cal.year, cal.month, 1).getDay()
    var daysInMonth = new Date(cal.year, cal.month + 1, 0).getDate()

    var html = ''
    var i
    for (i = 0; i < firstWd; i++) {
      html += '<button type="button" class="gim-cal-cell is-empty" tabindex="-1" aria-hidden="true"></button>'
    }
    for (i = 1; i <= daysInMonth; i++) {
      var date = new Date(cal.year, cal.month, i)
      date.setHours(0, 0, 0, 0)
      var cls = 'gim-cal-cell'
      if (date.getTime() === today.getTime()) cls += ' is-today'
      if (selected && date.getTime() === selected.getTime()) cls += ' is-selected'
      var disabled = date < today
      html += '<button type="button" class="' + cls + '" data-date="' + fmtISO(date) + '"' +
        (disabled ? ' disabled' : '') +
        ' aria-label="' + esc(fmtISO(date) + ' ' + WEEKDAY_NAMES[date.getDay()]) + '">' + i + '</button>'
    }
    var tail = (7 - ((firstWd + daysInMonth) % 7)) % 7
    for (i = 0; i < tail; i++) {
      html += '<button type="button" class="gim-cal-cell is-empty" tabindex="-1" aria-hidden="true"></button>'
    }
    gridEl.innerHTML = html
  }

  function onCalClick(e) {
    var link = e.target.closest('[data-cal]')
    if (link) {
      var action = link.getAttribute('data-cal')
      if (action === 'prev') {
        cal.month--
        if (cal.month < 0) { cal.month = 11; cal.year-- }
        renderCal()
      } else if (action === 'next') {
        cal.month++
        if (cal.month > 11) { cal.month = 0; cal.year++ }
        renderCal()
      } else if (action === 'today') {
        var t = todayFloor()
        cal.year = t.getFullYear()
        cal.month = t.getMonth()
        pickDate(t)
      } else if (action === 'clear') {
        setDate('')
        closeCal()
        refs.dateBtn.focus()
      }
      return
    }
    var cell = e.target.closest('.gim-cal-cell')
    if (cell && !cell.disabled && cell.getAttribute('data-date')) {
      var d = parseISO(cell.getAttribute('data-date'))
      if (d) pickDate(d)
    }
  }

  function pickDate(date) {
    setDate(fmtISO(date))
    closeCal()
    if (refs.dateBtn) refs.dateBtn.focus()
  }

  function setDate(iso) {
    refs.date.value = iso
    values.travelDate = iso
    renderDateText()
  }

  /* ======================================================================
     校验
     ====================================================================== */

  function clearFieldError(el) {
    el.removeAttribute('aria-invalid')
    var errId = el.getAttribute('aria-describedby')
    if (!errId) return
    var err = document.getElementById(errId)
    if (err) {
      err.textContent = ''
      err.classList.remove('is-show')
    }
  }

  function setFieldError(el, errEl, msg) {
    if (!errEl) return
    el.setAttribute('aria-invalid', 'true')
    errEl.textContent = msg
    errEl.classList.add('is-show')
  }

  function validate() {
    var L = cfg.labels
    var firstBad = null

    var name = refs.name.value.trim()
    if (!name) { setFieldError(refs.name, refs.nameErr, L.errRequired); firstBad = firstBad || refs.name }
    else clearFieldError(refs.name)

    var email = refs.email.value.trim()
    if (!email) { setFieldError(refs.email, refs.emailErr, L.errRequired); firstBad = firstBad || refs.email }
    else if (!isEmail(email)) { setFieldError(refs.email, refs.emailErr, L.errEmail); firstBad = firstBad || refs.email }
    else clearFieldError(refs.email)

    // 电话按「阿拉伯数字」输入，这里以位数判断有效性（含国际区号时最长 15 位）
    var phone = onlyDigits(refs.phone.value)
    if (!phone) { setFieldError(refs.phone, refs.phoneErr, L.errRequired); firstBad = firstBad || refs.phone }
    else if (phone.length < 5 || phone.length > 15) {
      setFieldError(refs.phone, refs.phoneErr, L.errPhone)
      firstBad = firstBad || refs.phone
    } else clearFieldError(refs.phone)

    if (firstBad) firstBad.focus()
    return !firstBad
  }

  /* ======================================================================
     提交
     ====================================================================== */

  /**
   * 组装提交体。字段名与取值严格对齐 prompt/API接入.md 的接口约定：
   * snake_case 命名、区号(country_code)与号码(phone)分开、酒店星级用中文（"4星"）。
   */
  function collect() {
    return {
      name: refs.name.value.trim(),
      nationality: refs.nationality.value.trim(),
      email: refs.email.value.trim(),
      country_code: selectedDialCode(),
      phone: onlyDigits(refs.phone.value),
      // 接口靠这两个字段知道「问的是哪个团」，缺了后端无法归档
      product_name: cfg.productName || '',
      product_code: cfg.productCode || '',
      travel_date: refs.date.value || '',
      adults: refs.adults.value,
      children: refs.children.value,
      babies: refs.babies.value,
      hotel_standard: checkedHotel(),
      message: refs.message.value.trim(),
    }
  }

  /** option 的 value 是 ISO 二字代码（区号会重复），提交时从 data-dial 取回区号 */
  function selectedDialCode() {
    if (!refs.phoneCode) return ''
    var opt = refs.phoneCode.options[refs.phoneCode.selectedIndex]
    return (opt && opt.getAttribute('data-dial')) || ''
  }

  function checkedHotel() {
    var boxes = elPopup.querySelectorAll('input[name="hotel_standard"]')
    for (var i = 0; i < boxes.length; i++) {
      if (boxes[i].checked) return boxes[i].value
    }
    return ''
  }

  function showStatus(kind, msg) {
    if (!refs.status) return
    refs.status.className = 'gim-status is-show is-' + kind
    refs.status.textContent = msg
  }

  function clearStatus() {
    if (!refs.status) return
    refs.status.className = 'gim-status'
    refs.status.textContent = ''
  }

  function setSubmitting(on) {
    submitting = on
    if (!refs.submit) return
    refs.submit.disabled = on
    refs.submit.innerHTML = on
      ? '<span class="gim-spinner" aria-hidden="true"></span>' + esc(cfg.labels.submitting)
      : esc(cfg.labels.submit)
  }

  function showSuccess() {
    if (!refs.form || !refs.success) return
    // 提交成功后清空表单，否则 close() 里的 readForm 会把已提交的内容当成
    // 「未提交的草稿」存回 values，重开时又原样回填
    refs.form.reset()
    setDate('')
    values = {}
    refs.form.hidden = true
    refs.success.hidden = false
    var btn = refs.success.querySelector('.gim-submit')
    if (btn) btn.focus()
  }

  function showForm() {
    if (!refs.form || !refs.success) return
    refs.success.hidden = true
    refs.form.hidden = false
  }

  function onSubmit(e) {
    e.preventDefault()
    if (submitting) return
    if (!validate()) return

    if (!cfg.apiUrl) {
      // 静默失败会让用户以为按钮坏了，这里必须给出可见反馈
      console.error('[GroupInquiryModal] 未配置 api_url，表单无法提交。' +
        '请在 section / snippet 里传入 api_url 参数。')
      showStatus('error', cfg.labels.errNoApi)
      return
    }

    clearStatus()
    setSubmitting(true)

    fetch(cfg.apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(collect()),
    })
      .then(function (res) {
        // 接口的响应体是 {code, msg}，校验失败时还带 errors 数组
        // （如 {"code":422,"msg":"参数校验失败","errors":["name 是必填项"]}）。
        // 把它转达出来，比笼统的「提交失败」有用得多 —— 跨域被拦时 msg 也会
        // 直接说明原因（"来源域名未被允许" = 当前域名不在白名单）。
        return res.text().then(function (text) {
          var data = null
          try { data = JSON.parse(text) } catch (_) {}
          var code = data && data.code != null ? Number(data.code) : null
          if (res.ok && (code === null || code < 400)) return
          var detail = data && data.errors && data.errors.length
            ? data.errors.join(' / ')
            : ((data && data.msg) || ('HTTP ' + res.status))
          var e = new Error(detail)
          e.serverMsg = detail
          throw e
        })
      })
      .then(function () {
        // 弹窗可能在请求期间被关掉（组件已销毁），此时不再动 DOM
        if (!elPopup) return
        setSubmitting(false)
        showSuccess()
      })
      .catch(function (err) {
        if (!elPopup) return
        setSubmitting(false)
        // 服务端明说了原因就照实转达，否则（含跨域被拦）退回通用文案
        showStatus('error', (err && err.serverMsg) || cfg.labels.errNetwork)
        console.error('[GroupInquiryModal] 提交失败：', err)
      })
  }

  function onPhoneInput(e) {
    var input = e.target
    var digits = onlyDigits(input.value)
    if (input.value !== digits) input.value = digits
    values.phone = digits
    clearFieldError(input)
  }

  /* ======================================================================
     挂载 / 销毁
     ====================================================================== */

  function onPopupClick(e) {
    if (e.target.closest('[data-gim-close]')) {
      e.preventDefault()
      close()
    }
  }

  function onKeydown(e) {
    if (!elPopup) return
    if (e.key === 'Escape') {
      if (calEl) { closeCal(); return }
      close()
      return
    }
    if (e.key !== 'Tab') return
    trapFocus(e)
  }

  function focusables() {
    var sel = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),' +
              'textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
    var out = []
    var nodes = elPopup.querySelectorAll(sel)
    var i
    for (i = 0; i < nodes.length; i++) {
      // 用 getClientRects 而不是 offsetParent：后者对 position:fixed 恒为 null
      if (nodes[i].getClientRects().length) out.push(nodes[i])
    }
    if (calEl) {
      var calNodes = calEl.querySelectorAll(sel)
      for (i = 0; i < calNodes.length; i++) {
        if (calNodes[i].getClientRects().length) out.push(calNodes[i])
      }
    }
    return out
  }

  function trapFocus(e) {
    var list = focusables()
    if (!list.length) return
    var first = list[0]
    var last = list[list.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  function mount() {
    if (elPopup) return
    elPopup = document.createElement('div')
    elPopup.id = POPUP_ID
    elPopup.className = 'gim-popup'
    elPopup.setAttribute('role', 'dialog')
    elPopup.setAttribute('aria-modal', 'true')
    elPopup.setAttribute('aria-labelledby', 'gimTitle')
    elPopup.innerHTML = buildMarkup()
    document.body.appendChild(elPopup)

    cacheRefs()
    applyValues()

    elPopup.addEventListener('click', onPopupClick)
    refs.form.addEventListener('submit', onSubmit)
    refs.form.addEventListener('input', onFormInput)
    refs.form.addEventListener('change', onFormInput)
    refs.phone.addEventListener('input', onPhoneInput)
    refs.dateBtn.addEventListener('click', openCal)
    document.addEventListener('keydown', onKeydown)

    prevBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }

  /** 释放资源：移除节点、解绑监听、恢复页面滚动。表单值保留在内存里供重开回填。 */
  function release() {
    if (!elPopup) return
    closeCal()
    readForm()

    elPopup.removeEventListener('click', onPopupClick)
    if (refs.form) {
      refs.form.removeEventListener('submit', onSubmit)
      refs.form.removeEventListener('input', onFormInput)
      refs.form.removeEventListener('change', onFormInput)
    }
    if (refs.phone) refs.phone.removeEventListener('input', onPhoneInput)
    if (refs.dateBtn) refs.dateBtn.removeEventListener('click', openCal)
    document.removeEventListener('keydown', onKeydown)

    elPopup.remove()
    elPopup = null
    refs = {}
    valKeyMap = {}
    radioGroups = {}
    submitting = false
    document.body.style.overflow = prevBodyOverflow
    prevBodyOverflow = ''
  }

  /* ======================================================================
     对外行为
     ====================================================================== */

  /** 标题 / 产品编号 / 接口变了，就必须重建 DOM，否则会挂着上一个产品的信息。 */
  function sameConfig(a, b) {
    return a.productName === b.productName &&
           a.productCode === b.productCode &&
           a.apiUrl === b.apiUrl
  }

  function open(config) {
    var next = config ? normalizeConfig(config) : cfg
    // 同一页可能存在多个不同产品的入口（如列表页每张卡片一个）。若弹窗还开着就被
    // 切到另一个产品，必须重建 —— mount() 对已存在的节点是直接返回的。
    // release() 会先把当前表单值读进 values，重建后由 applyValues() 回填，草稿不丢。
    if (elPopup && !sameConfig(cfg, next)) release()
    cfg = next

    mount()
    if (!elPopup) return

    showForm()
    clearStatus()
    setSubmitting(false)

    lastFocused = document.activeElement
    elPopup.classList.add('is-open')

    // 打开后落到第一个还没填的必填项，直接可以开始打字。
    // 若是「草稿已填满」的情况就没得可落，退回到 dialog 本身 —— 否则焦点会一直
    // 停在弹窗外的触发按钮上，屏幕阅读器既读不到弹窗标题，键盘用户也进不来。
    var target = null
    if (!refs.name.value.trim()) target = refs.name
    else if (!refs.email.value.trim()) target = refs.email
    else if (!refs.phone.value) target = refs.phone
    else target = refs.dialog
    if (target) {
      try { target.focus({ preventScroll: true }) } catch (_) { target.focus() }
    }
  }

  function close() {
    if (!elPopup) return
    release()
    // 触发入口可能已被页面重绘掉，先确认还在文档里再还焦点
    if (lastFocused && lastFocused.focus && document.contains(lastFocused)) {
      try { lastFocused.focus({ preventScroll: true }) } catch (_) { lastFocused.focus() }
    }
    lastFocused = null
  }

  /** 只记录默认配置，不建 DOM —— 真正的节点在第一次 open 时才创建。 */
  function init(config) {
    if (config) cfg = normalizeConfig(config)
  }

  /* ======================================================================
     入口委托：整页只绑一次，之后新增多少入口都自动生效
     ====================================================================== */

  function findConfig(code) {
    var list = window.__gimConfigs || []
    for (var i = 0; i < list.length; i++) {
      if (code && String(list[i].productCode) === String(code)) return list[i]
    }
    return null
  }

  function onDocClick(e) {
    var target = e.target
    if (!target || !target.closest) return
    var btn = target.closest('[data-gim-trigger]')
    if (!btn) return
    e.preventDefault()
    var code = btn.getAttribute('data-gim-code') || ''
    open(findConfig(code) || window.__gimInitConfig || {})
  }

  document.addEventListener('click', onDocClick)

  // 回退 / 关闭页面即释放弹窗资源（pagehide 同时覆盖 bfcache 进出）
  window.addEventListener('pagehide', function () {
    if (elPopup) release()
  })

  window.GroupInquiryModal = {
    init: init,
    open: open,
    close: close,
    release: release,
    isOpen: function () { return !!(elPopup && elPopup.classList.contains('is-open')) },
    getConfig: function () { return cfg },
  }

  // Liquid 注入的默认配置：有就记下来，但仍然等点击才建 DOM
  if (window.__gimInitConfig) {
    cfg = normalizeConfig(window.__gimInitConfig)
  }
})()
